import { useCallback, useEffect, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useCartWatcher } from "../func";
import { useElementVisibility } from "../hook/use-element-visibility";

type CartGoalData = {
  discount: number;
  value?: number;
  count?: number;
  couponCode: string;
};

export default function CartGoals({
  type,
  data,
}: {
  type: "order-value" | "product-count";
  data: any;
}) {
  const [progress, setProgress] = useState(0);
  const [animationParent] = useAutoAnimate();
  const [message, setMessage] = useState("");

  const [elementRef] = useElementVisibility<HTMLDivElement>({
    data: {
      name: "cart-goals",
      properties: {
        category: "cart-goals",
      },
    },
  });

  const calculateTotalQuantity = (cart: any) => {
    if (!cart?.items) return 0;
    return cart.items.reduce(
      (sum: number, item: any) => sum + item.quantity,
      0,
    );
  };

  const handleCartUpdate = useCallback(() => {
    const cart = window.helium.datastore.memory.cart;
    const total = calculateTotalQuantity(cart);
    let currentMilestone: CartGoalData | undefined;
    let nextMilestone: CartGoalData | undefined;

    for (const goal of data) {
      if (
        (type === "order-value" &&
          goal.value !== undefined &&
          total >= goal.value) ||
        (type === "product-count" &&
          goal.count !== undefined &&
          total >= goal.count)
      ) {
        currentMilestone = goal;
      } else if (
        (type === "order-value" &&
          goal.value !== undefined &&
          total < goal.value) ||
        (type === "product-count" &&
          goal.count !== undefined &&
          total < goal.count)
      ) {
        nextMilestone = goal;
        break;
      }
    }
    const max =
      type === "order-value"
        ? Math.max(...data.map((item: any) => item.value))
        : Math.max(...data.map((item: any) => item.count));
    const progressPercentage = Math.min((total / max) * 100, 100);
    setProgress(progressPercentage);
    if (currentMilestone && !nextMilestone) {
      setMessage(
        `Congratulations! You are eligible for a ${currentMilestone.discount}% discount!`,
      );
    } else if (nextMilestone) {
      const remainingValue =
        type === "order-value"
          ? nextMilestone.value! - total
          : nextMilestone.count! - total;
      setMessage(
        `You are just ${remainingValue} ${
          type === "order-value" ? "value" : "product"
        } away from ${nextMilestone.discount}% discount!`,
      );
    }
  }, [data, type]);

  useCartWatcher(handleCartUpdate);

  useEffect(() => {
    handleCartUpdate();
  }, [handleCartUpdate]);

  return (
    <>
      <h5 className="mx-auto w-fit px-0 text-center text-base text-[#3300C3]">
        {message}
      </h5>
      <div ref={elementRef} className="hp-mx-auto hp-w-full hp-px-0 hp-py-2">
        <div className="hp-flex hp-justify-between hp-text-sm hp-text-gray-600">
          {type === "order-value" ? (
            <>
              {data.map((item) => (
                <span>{item.value}</span>
              ))}
            </>
          ) : (
            <>
              {data.map((item) => (
                <span>{item.count} products</span>
              ))}
            </>
          )}
        </div>
        <div className="hp-relative hp-py-2" ref={animationParent}>
          <div className="flex h-1 overflow-hidden rounded bg-[#8B8B8B40] bg-opacity-25 text-xs">
            <div
              style={{ width: `${progress}%` }}
              className="flex flex-col justify-center whitespace-nowrap bg-[#3300C3] text-center text-white shadow-none transition-all duration-300 ease-in-out"
            ></div>
          </div>
        </div>
        <div className="hp-flex hp-justify-between hp-text-sm hp-text-gray-600">
          {data.map((item) => (
            <span>
              {item.discount === 0 ? "No" : item.discount + "%"} discount
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
